import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { validarFormulario } from "../utils/validators";
import { formatearFecha } from "../utils/formatters";
import { estadoColor } from "../utils/estadoColor";
import { createTicket, avanzarTicket, retrocederTicket, updateTicket, createPresupuesto, updateEquipo } from "../services/dataService";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import SectionHeader from "../components/ui/SectionHeader";

export default function ModuloTickets({ 
  tickets, setTickets, equipos, setEquipos, 
  usuarios = [], presupuestos, 
  setPresupuestos, setTab, agregarAlHistorial,
  onNotify, agregarNotificacion, registrarAuditoria 
}) {
  const { puede, usuario } = useAuth();
  // ✅ ELIMINADO "Cerrado" - El flujo termina en "Entregado"
  const ESTADOS = ["Recepción", "Diagnóstico", "Presupuesto", "Reparación", "Reparado", "Entregado"];
  
  const [selected, setSelected] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("Todos");
  const [filtroPrioridad, setFiltroPrioridad] = useState("Todos");
  const [nuevoTicket, setNuevoTicket] = useState(false);
  
  const [formData, setFormData] = useState({
    equipoId: "",
    tecnico: usuario?.rol === "Técnico" ? usuario.nombre : "",
    prioridad: "Media",
    descripcion: "",
    fecha: new Date().toISOString().slice(0, 10)
  });
  const [errors, setErrors] = useState({});

  const equipo = (eid) => equipos.find(e => String(e.id) === String(eid));

  // 🔄 FUNCIÓN PARA ACTUALIZAR ESTADO DEL EQUIPO SEGÚN TICKET
  const sincronizarEstadoEquipo = (ticketId, nuevoEstadoTicket) => {
    if (!setEquipos) return;
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket?.equipoId) return;
    const eq = equipos.find(e => e.id === ticket.equipoId);
    if (!eq) return;
    
    const estadoEquipoPorTicket = {
      "Recepción": "Recepción",
      "Diagnóstico": "Diagnóstico", 
      "Presupuesto": "En Presupuesto", 
      "Reparación": "En reparación",
      "Reparado": "Reparado",
      "Entregado": "Entregado"
      // ✅ ELIMINADO: "Cerrado": "Entregado"
    };
    
    const nuevoEstadoEquipo = estadoEquipoPorTicket[nuevoEstadoTicket];
    
    if (nuevoEstadoEquipo && eq.estado !== nuevoEstadoEquipo) {
      setEquipos(prev => prev.map(e => 
        e.id === ticket.equipoId ? { ...e, estado: nuevoEstadoEquipo } : e
      ));
      onNotify?.(`Equipo ${eq.serie} actualizado a "${nuevoEstadoEquipo}"`, "info");
    }
  };

  const avanzar = async (id) => {
    if (!puede("editar")) { 
      onNotify?.("No tenés permiso para modificar tickets", "error"); 
      return; 
    }

    const ticket = tickets.find(t => t.id === id);
    if (!ticket) return;

    const idx = ESTADOS.indexOf(ticket.estado);
    const next = ESTADOS[Math.min(idx + 1, ESTADOS.length - 1)];

    if (ticket.estado === "Entregado") {
      onNotify?.("✅ Ticket ya entregado", "info");
      return;
    }

    const presupuestoVinculado = presupuestos.find(p => p.ticketId === ticket.id);
    if (presupuestoVinculado && presupuestoVinculado.estado !== "Aprobado") {
      onNotify?.(`⛔ No se puede avanzar: El presupuesto está "${presupuestoVinculado.estado}".`, "error");
      return;
    }

    if (next === "Entregado" && !puede("aprobar")) {
      onNotify?.("⛔ Solo Administradores o Calidad pueden entregar equipos.", "error");
      return;
    }

    if (next !== ticket.estado) {
      try {
        await avanzarTicket(id, next, ticket.equipoId);
        setTickets(prev => prev.map(t => t.id === id ? { ...t, estado: next } : t));
        sincronizarEstadoEquipo(id, next);
        registrarAuditoria?.(`Ticket #${ticket.id} → ${next}`, "Tickets");
        if (agregarAlHistorial) {
          const eq = equipos.find(e => e.id === ticket.equipoId);
          agregarAlHistorial(ticket.equipoId, eq?.serie || "S/N", next, `Ticket #${ticket.id} → ${next}`, ticket.tecnico || usuario?.nombre, []);
        }
      } catch (e) {
        onNotify?.(e.message, "error");
        return;
      }
    }
    onNotify?.("Estado del ticket actualizado", "success");
  };

  const retroceder = async (id) => {
    if (!puede("editar")) return;
    const ticket = tickets.find(t => t.id === id);
    if (!ticket) return;
    const idx = ESTADOS.indexOf(ticket.estado);
    const prevEstado = ESTADOS[Math.max(idx - 1, 0)];
    if (prevEstado !== ticket.estado) {
      try {
        await retrocederTicket(id, ticket.equipoId);
        setTickets(prev => prev.map(t => t.id === id ? { ...t, estado: prevEstado } : t));
        sincronizarEstadoEquipo(id, prevEstado);
      } catch (e) {
        onNotify?.(e.message, "error");
        return;
      }
    }
    onNotify?.("Estado retrocedido", "info");
  };

  const filtrados = tickets
    .filter(t => {
      if (filtroEstado !== "Todos" && t.estado !== filtroEstado) return false;
      if (filtroPrioridad !== "Todos" && t.prioridad !== filtroPrioridad) return false;
      return true;
    })
    .sort((a, b) => {
      const diffId = b.id - a.id;
      if (diffId !== 0) return diffId;
      return new Date(b.fecha) - new Date(a.fecha);
    });

  const crearTicket = async () => {
    if (!puede("crear")) { 
      onNotify?.("No tenés permiso", "error"); 
      return; 
    }
    
    const errores = validarFormulario(formData, ['equipoId']); 
    const hayTecnicos = usuarios.some(u => u.activo === true || u.activo === 1 || u.activo === "1");
    if (hayTecnicos && !formData.tecnico) {
       errores.tecnico = "Asigná un técnico";
    }

    if (Object.keys(errores).length > 0) {
      setErrors(errores);
      onNotify?.("Completá los campos obligatorios", "warning");
      return;
    }

    try {
      const saved = await createTicket({ ...formData, estado: "Recepción", creado_por: usuario?.nombre || "Sistema" });
      setTickets(prev => [...prev, saved]);
      registrarAuditoria?.(`Ticket #${saved.id} creado - ${formData.prioridad}`, "Tickets");
      if (agregarNotificacion && formData.tecnico) {
        agregarNotificacion(`🎫 Nuevo ticket #${saved.id} asignado a ${formData.tecnico}`, "info");
      }
      if (formData.equipoId) sincronizarEstadoEquipo(saved.id, "Recepción");
      onNotify?.(`Ticket #${saved.id} creado`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
    setFormData({ equipoId: "", tecnico: usuario?.rol === "Técnico" ? usuario.nombre : "", prioridad: "Media", descripcion: "", fecha: new Date().toISOString().slice(0, 10) });
    setErrors({});
    setNuevoTicket(false);
  };

  const crearPresupuestoDesdeTicket = async (ticket) => {
    const eq = equipo(ticket.equipoId);
    const nuevoPresupuesto = {
      ticketId: ticket.id,
      cliente: eq?.cliente || "Sin cliente",
      domicilio: eq?.domicilio || "",
      monto: null,
      estado: "Pendiente",
      fecha: new Date().toISOString().slice(0, 10),
      items: [],
      observaciones: "",
      detalle: `Solicitud automática desde ticket #${ticket.id}`
    };

    try {
      const saved = await createPresupuesto(nuevoPresupuesto);
      setPresupuestos(prev => [...prev, saved]);
      await updateTicket({ id: ticket.id, estado: "Presupuesto" });
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, estado: "Presupuesto" } : t));
      registrarAuditoria?.(`Presupuesto #${saved.id} creado desde Ticket #${ticket.id}`, "Tickets");
      
      if (setEquipos && eq) {
        await updateEquipo({ id: eq.id, estado: "En Presupuesto" });
        setEquipos(prev => prev.map(e => e.id === eq.id ? {...e, estado: "En Presupuesto"} : e));
      }
      onNotify?.(`Solicitud creada. Cargá los materiales en Presupuestos.`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
    setTab("presupuestos");
  };

  // 🛠️ FILTRO ROBUSTO PARA TÉCNICOS
  const tecnicosDisponibles = usuarios.filter(u => {
    return u.activo === true || u.activo === 1 || u.activo === "1" || u.activo === "true";
  });

  return (
    <div>
      <SectionHeader 
        title={`Tickets (${filtrados.length})`} 
        subtitle="Gestioná el flujo de trabajo de las reparaciones" 
        action={puede("crear") && (
          <Btn variant="primary" onClick={() => { setNuevoTicket(true); setErrors({}); }}>
            + Nuevo Ticket
          </Btn>
        )} 
      />

      {nuevoTicket && (
        <Card className="form-card">
          <p className="form-card__title"> Crear Nuevo Ticket</p>
          <div className="grid grid--2 gap-2 mb-2">
            <Select 
              value={formData.equipoId} 
              onChange={e => { 
                setFormData(p => ({ ...p, equipoId: e.target.value })); 
                setErrors(pr => ({...pr, equipoId: null})); 
              }} 
              error={errors.equipoId}
            >
              <option value="">Seleccionar equipo *</option>
              {equipos.filter(e => !["Entregado", "Fuera de servicio"].includes(e.estado)).map(eq => (
                <option key={eq.id} value={eq.id}>{eq.serie} · {eq.modelo} ({eq.cliente})</option>
              ))}
            </Select>

            {/* 🛠️ SELECT DE TÉCNICOS CORREGIDO */}
            <Select 
              value={formData.tecnico} 
              onChange={e => { 
                setFormData(p => ({ ...p, tecnico: e.target.value })); 
                setErrors(pr => ({...pr, tecnico: null})); 
              }} 
              error={errors.tecnico}
            >
              <option value="">Asignar técnico</option>
              {tecnicosDisponibles.length > 0 ? (
                tecnicosDisponibles.map(t => (
                  <option key={t.id} value={t.nombre}>
                    {t.nombre} {t.rol ? `(${t.rol})` : ''}
                  </option>
                ))
              ) : (
                <option disabled>No hay usuarios activos. Andá a "Usuarios" para crear uno.</option>
              )}
            </Select>

            <Select 
              value={formData.prioridad} 
              onChange={e => setFormData(p => ({ ...p, prioridad: e.target.value }))}
            >
              <option value="Alta">🔴 Alta prioridad</option>
              <option value="Media">🟠 Media prioridad</option>
              <option value="Baja">🟢 Baja prioridad</option>
            </Select>
            <Input 
              value={formData.fecha} 
              onChange={e => setFormData(p => ({ ...p, fecha: e.target.value }))} 
              type="date" 
            />
            <Input 
              value={formData.descripcion} 
              onChange={e => setFormData(p => ({ ...p, descripcion: e.target.value }))} 
              placeholder="Descripción de la falla (opcional)" 
              className="col-span-2" 
            />
          </div>
          <div className="flex gap-2">
            <Btn variant="primary" onClick={crearTicket}>Crear Ticket</Btn>
            <Btn variant="default" onClick={() => { setNuevoTicket(false); setErrors({}); }}>Cancelar</Btn>
          </div>
        </Card>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        <Select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} className="w-auto">
          <option value="Todos">Todos los estados</option>
          {ESTADOS.map(est => <option key={est}>{est}</option>)}
        </Select>
        <Select value={filtroPrioridad} onChange={e => setFiltroPrioridad(e.target.value)} className="w-auto">
          <option value="Todos">Todas prioridades</option>
          {["Alta", "Media", "Baja"].map(p => <option key={p}>{p}</option>)}
        </Select>
      </div>

      <div className="grid gap-2">
        {filtrados.map(t => {
          const eq = equipo(t.equipoId);
          const idx = ESTADOS.indexOf(t.estado);
          const tienePresupuesto = presupuestos.some(p => p.ticketId === t.id);
          const presupuestoVinculado = presupuestos.find(p => p.ticketId === t.id);
          
          return (
            <Card 
              key={t.id} 
              className={`ticket-card ticket-card--${t.prioridad.toLowerCase()}`} 
              onClick={() => setSelected(selected === t.id ? null : t.id)}
            >
              <div className="ticket-card__header">
                <div className="flex gap-2 items-center flex-wrap">
                  <span className="font-semibold">#{t.id}</span>
                  <Badge color={t.prioridad === "Alta" ? "danger" : t.prioridad === "Media" ? "warning" : "muted"}>
                    {t.prioridad}
                  </Badge>
                  <Badge color={estadoColor(t.estado)}>{t.estado}</Badge>
                  
                  {tienePresupuesto && (
                    <Badge color={presupuestoVinculado?.estado === "Aprobado" ? "success" : "warning"}>
                      {presupuestoVinculado?.estado === "Aprobado" ? "✅ PRESUPUESTO OK" : "⏳ PRESUPUESTO PENDIENTE"}
                    </Badge>
                  )}
                </div>
                
                <div className="flex gap-2 items-center flex-wrap">
                  <span className="text-xs text-muted">👤 {t.tecnico || "Sin asignar"}</span>
                  
                  {/* ✅ ELIMINADO "Cerrado" - Solo verifica "Entregado" */}
                  {t.estado !== "Entregado" && puede("editar") && (
                    <>
                      {idx > 0 && (
                        <Btn small variant="default" onClick={(e) => { e.stopPropagation(); retroceder(t.id); }}>←</Btn>
                      )}
                      <Btn small onClick={(e) => { e.stopPropagation(); avanzar(t.id); }}>Avanzar →</Btn>
                    </>
                  )}
                  
                  {t.estado === "Presupuesto" && !tienePresupuesto && puede("crear") && (
                    <Btn small variant="warning" onClick={(e) => { e.stopPropagation(); crearPresupuestoDesdeTicket(t); }}>
                      💰 Presupuesto
                    </Btn>
                  )}
                </div>
              </div>
              
              {eq && (
                <p className="text-sm text-muted mt-1">
                  {eq.modelo} · {eq.serie} · {eq.cliente || "—"}
                </p>
              )}
              
              {selected === t.id && (
                <div className="ticket-card__details">
                  <p className="text-xs font-semibold mb-2">Progreso:</p>
                  <div className="progress-bar">
                    {ESTADOS.map((s, i) => (
                      <div key={s} className={`progress-step ${i <= idx ? "progress-step--active" : ""}`}>{s}</div>
                    ))}
                  </div>
                  <p className="text-xs text-muted mt-2">
                    Fecha: {formatearFecha(t.fecha)} · Creado por: {t.creado_por}
                  </p>
                </div>
              )}
            </Card>
          );
        })}
        {filtrados.length === 0 && (
          <p className="text-muted text-center py-5">No hay tickets. Creá uno nuevo arriba ☝️</p>
        )}
      </div>
    </div>
  );
}